import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPujas,
  addPuja as addPujaService,
  updatePuja as updatePujaService,
  deletePuja as deletePujaService,
  updatePujaStatus as updatePujaStatusService,
} from "../../services/pujaService";
import { toast } from "react-toastify";

const initialState = {
  pujas: [],
  status: "idle", // "idle" | "loading" | "succeeded" | "failed"
  error: null,
  totalCount: 0,
};

// Fetch all pujas
export const fetchPujas = createAsyncThunk(
  "pujas/fetchPujas",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await getPujas(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch pujas";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Add a new puja
export const addPuja = createAsyncThunk(
  "pujas/addPuja",
  async (pujaData, { rejectWithValue }) => {
    try {
      const response = await addPujaService(pujaData);
      toast.success(response.message || "Puja added successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to add puja";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a puja
export const updatePuja = createAsyncThunk(
  "pujas/updatePuja",
  async ({ id, pujaData }, { rejectWithValue }) => {
    try {
      const response = await updatePujaService(id, pujaData);
      toast.success(response.message || "Puja updated successfully!");
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update puja";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Update a puja status
export const updatePujaStatus = createAsyncThunk(
  "pujas/updatePujaStatus",
  async ({ id, statusObj }, { rejectWithValue }) => {
    try {
      const response = await updatePujaStatusService(id, statusObj);
      toast.success(response.message || "Status updated successfully!");
      return { id, statusObj, updatedData: response.data };
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to update status";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// Delete a puja
export const deletePuja = createAsyncThunk(
  "pujas/deletePuja",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deletePujaService(id);
      toast.success(response.message || "Puja deleted successfully!");
      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to delete puja";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const pujaSlice = createSlice({
  name: "pujas",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetPujaState: (state) => {
      state.pujas = [];
      state.status = "idle";
      state.error = null;
      state.totalCount = 0;
    },
    setPujas: (state, action) => {
      state.pujas = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Pujas
      .addCase(fetchPujas.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })


      // .addCase(fetchPujas.fulfilled, (state, action) => {
      //   state.status = "succeeded";
      //   state.pujas = action.payload.data || [];
      //   state.totalCount = action.payload.totalCount || action.payload.data?.length || 0;
      //   state.error = null;
      // })


      .addCase(fetchPujas.fulfilled, (state, action) => {
    state.status = "succeeded";

    // backend response:
    // { data: [...], pagination: {...} }
    state.pujas = action.payload.data || [];

    // store backend pagination
    state.pagination = action.payload.pagination || {
        total: state.pujas.length,
        page: 1,
        limit: state.pujas.length,
        totalPages: 1
    };

    // keep compatibility
    state.totalCount = state.pagination.total;

    state.error = null;
})


      .addCase(fetchPujas.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.pujas = [];
      })

      // Add Puja
      .addCase(addPuja.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addPuja.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload.data) {
          // Add the new puja at the beginning of the array
          state.pujas.unshift(action.payload.data);
          state.totalCount += 1;
        }
        state.error = null;
      })
      .addCase(addPuja.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Puja
      .addCase(updatePuja.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updatePuja.fulfilled, (state, action) => {
        state.status = "succeeded";
        const updatedPuja = action.payload.data;

        if (updatedPuja && Array.isArray(state.pujas)) {
          const index = state.pujas.findIndex((puja) => puja._id === updatedPuja._id);
          if (index !== -1) {
            // Replace the entire puja object with the updated one
            state.pujas[index] = updatedPuja;
          }
        }
        state.error = null;
      })
      .addCase(updatePuja.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // Update Puja Status
      .addCase(updatePujaStatus.pending, (state) => {
        // Don't change overall status for status updates
      })
      .addCase(updatePujaStatus.fulfilled, (state, action) => {
        const { id, statusObj, updatedData } = action.payload;

        if (Array.isArray(state.pujas)) {
          const index = state.pujas.findIndex((puja) => puja._id === id);
          if (index !== -1) {
            // If we have updated data from server, use it; otherwise just update status
            if (updatedData) {
              state.pujas[index] = updatedData;
            } else {
              state.pujas[index] = { ...state.pujas[index], ...statusObj };
            }
          }
        }
      })
      .addCase(updatePujaStatus.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete Puja
      .addCase(deletePuja.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deletePuja.fulfilled, (state, action) => {
        state.status = "succeeded";
        const deletedId = action.payload;
        state.pujas = state.pujas.filter((puja) => puja._id !== deletedId);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.error = null;
      })
      .addCase(deletePuja.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      });
  },
});

export const { clearError, resetPujaState, setPujas } = pujaSlice.actions;

// Selectors
export const selectAllPujas = (state) => state.pujas.pujas;
export const selectPujaById = (state, pujaId) => 
  state.pujas.pujas.find(puja => puja._id === pujaId);
export const selectPujaStatus = (state) => state.pujas.status;
export const selectPujaError = (state) => state.pujas.error;
export const selectPujaTotalCount = (state) => state.pujas.totalCount;
export const selectActivePujas = (state) => 
  state.pujas.pujas.filter(puja => puja.status === 'Active');
export const selectPopularPujas = (state) => 
  state.pujas.pujas.filter(puja => puja.isPopular === true);
export const selectPujaPagination = (state) => state.pujas.pagination;


export default pujaSlice.reducer;

